import React, { FC, useContext, useEffect, useState } from 'react';

import { navigate } from '@reach/router';
import queryString from 'query-string';

import { GlobalContext } from '../../../store';
import { ILoginForm } from '../../../store/auth/actions';
import { IMessageNotification } from '../../../store/notification/store';

import Button from '../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../components/Forms';
import Input from '../../../components/Input';
import { redWaglLogo } from '../../../assets/images/';
import useLocation from '../../../contrib/Hooks/useLocation.hook';
import { strongPassWordErrorMessage } from '../../../contrib/validations';

interface IProps {
  invitationId?: string;
}

const Login: FC<IProps> = props => {
  const { invitationId } = props;

  const {
    actions: { auth, adminClubs, invitation, notifications }
  } = useContext(GlobalContext);

  const {
    location: { search }
  } = useLocation();

  const [queryStringEmail, setQueryStringEmail] = useState<string | null>();

  const onSubmit = async (data: ILoginForm): Promise<void> => {
    if (!!queryStringEmail && !data.email) {
      data.email = queryStringEmail;
    }

    const queryParams = queryString.parse(search);
    const response = await auth.login(data);

    if (response.errors) {
      await notifications.push(({
        status: 'unread',
        text: response.errors.login.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      if (response.auth && response.auth.isAuthenticated) {
        if (location && location.pathname === '/staff/login') {
          const { adminClubs: adminClus } = await adminClubs.all();

          if (adminClus && Object.keys(adminClus.all).length === 0) {
            navigate(`/staff/club/create/info`);
          } else {
            navigate(`/staff/home`);
          }
        } else if (queryParams.next) {
          navigate(Array.isArray(queryParams.next) ? queryParams.next[0] : queryParams.next);
        } else if (invitationId) {
          const invitationResponse = await invitation.get(invitationId);

          if (invitationResponse.invitation && invitationResponse.invitation.event) {
            navigate(`/event/${invitationResponse.invitation.event}`);
          } else if (invitationResponse.invitation && invitationResponse.invitation.club) {
            navigate(
              invitationId ? `/club-payment/${invitationResponse.invitation.club.id}` : '/home'
            );
          }
        } else {
          navigate(`/home`);
        }
      }
    }
  };

  const goToRequestNewPassword = () => {
    const section = location && location.href.includes('staff') ? '/staff' : '';

    navigate(`${section}/requestResetPassword`);
  };

  useEffect(() => {
    const newQueryStringEmail = new URLSearchParams(search).get('email');

    setQueryStringEmail(
      newQueryStringEmail ? newQueryStringEmail.replace(' ', '+') : newQueryStringEmail
    );
  }, [search]);

  return (
    <FormProvider
      defaultValues={{ email: queryStringEmail, password: 'b' }}
      onSubmit={onSubmit}
      validations={{
        email: 'required',
        password: ['required', 'password']
      }}
    >
      <FormConsumer>
        {({ formActions: { setValue }, errors }: IFormAttributes) => (
          <div className="login form wrapper">
            <div>
              <div className="logo-wrapper">
                <img src={redWaglLogo} className="logo" alt="logo" />
              </div>

              <h1>Welcome back</h1>

              {queryStringEmail !== undefined && (
                <Input
                  label="EMAIL"
                  id="email"
                  type="email"
                  onChange={setValue('email')}
                  hasError={errors.email}
                  errorMessage="You must provide a valid email"
                  fullWidth={true}
                  defaultValue={queryStringEmail}
                />
              )}

              <Input
                label="PASSWORD"
                id="password"
                type="password"
                onChange={setValue('password')}
                hasError={errors.password}
                errorMessage={strongPassWordErrorMessage}
                fullWidth={true}
              />

              <Button
                id="login"
                variant="contained"
                color="primary"
                disabled={errors.FORM}
                type="submit"
                className="button button-shadow"
              >
                LOGIN
              </Button>

              <Button
                onClick={goToRequestNewPassword}
                id="reset-password"
                color="primary"
                type="button"
                className="button button-shadow"
              >
                RESET MY PASSWORD
              </Button>
            </div>
          </div>
        )}
      </FormConsumer>
    </FormProvider>
  );
};

export { Login };
